// AdbTimeMonitorModal.js
// Modal to display ADB time logs for monitored devices on a specific router

import React, { useEffect, useState } from 'react';
import { getSocket } from './socket';

export default function AdbTimeMonitorModal({ routerName, onClose }) {
  const [monitoredDevices, setMonitoredDevices] = useState([]);
  const [logs, setLogs] = useState({ device91: [], device95: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monitorInterval, setMonitorInterval] = useState('5'); // in minutes
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalPages91: 1,
    totalPages95: 1,
    totalCount91: 0,
    totalCount95: 0,
    limit: 10,
  });

  async function fetchMonitoredDevices() {
    try {
      const response = await fetch('/api/monitored-devices', {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch monitored devices');
      const data = await response.json();

      // Filter devices for this router
      const devicesForRouter = data.devices.filter(d => d.routerName === routerName);
      console.log('[AdbTimeMonitor] Monitored devices for', routerName, ':', devicesForRouter);
      setMonitoredDevices(devicesForRouter);
    } catch (err) {
      console.error('[AdbTimeMonitor] Error fetching monitored devices:', err);
      setError('Failed to load monitored devices');
    }
  }

  useEffect(() => {
    fetchMonitoredDevices();
    fetchLogs(currentPage);
    checkMonitoringStatus();

    // Listen for real-time Socket.IO updates
    const socket = getSocket();
    if (socket) {
      console.log('[AdbTimeMonitor] Socket found, connected:', socket.connected);

      // Subscribe to updates immediately if already connected
      if (socket.connected) {
        socket.on('adb-log-update', handleSocketUpdate);
        console.log('[AdbTimeMonitor] Subscribed to Socket.IO adb-log-update events');
      } else {
        // Wait for connection, then subscribe
        console.log('[AdbTimeMonitor] Socket not yet connected, waiting for connect event');
        socket.once('connect', () => {
          console.log('[AdbTimeMonitor] Socket connected, subscribing to adb-log-update');
          socket.on('adb-log-update', handleSocketUpdate);
        });
      }
    } else {
      console.warn('[AdbTimeMonitor] Socket not available, using polling fallback');
    }

    // Fallback polling every 10 seconds if socket not available or not connected
    const pollInterval = setInterval(() => {
      if (!socket || !socket.connected) {
        console.log('[AdbTimeMonitor] Polling for updates (socket unavailable)');
        fetchLogs(currentPage);
      }
    }, 10000);

    // Cleanup: unsubscribe from Socket.IO events and clear interval
    return () => {
      if (socket) {
        socket.off('adb-log-update', handleSocketUpdate);
        socket.off('connect', handleSocketUpdate);
        console.log('[AdbTimeMonitor] Unsubscribed from Socket.IO events');
      }
      clearInterval(pollInterval);
    };
  }, [currentPage]);

  // Handle real-time Socket.IO updates
  function handleSocketUpdate(data) {
    const { deviceIp, log } = data;
    console.log('[AdbTimeMonitor] Received real-time update for', deviceIp, log);

    // Only update if we're on page 1 (most recent logs)
    if (currentPage === 1) {
      setLogs((prevLogs) => {
        const deviceKey = deviceIp === '10.5.1.91' ? 'device91' : 'device95';
        return {
          ...prevLogs,
          [deviceKey]: [log, ...prevLogs[deviceKey]].slice(0, pagination.limit),
        };
      });
    }
  }

  async function checkMonitoringStatus() {
    try {
      const response = await fetch('/api/adb-monitor/status', {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setIsMonitoring(data.isMonitoring);
      }
    } catch (err) {
      console.error('Failed to check monitoring status:', err);
    }
  }

  async function fetchLogs(page = 1) {
    try {
      const response = await fetch(`/api/adb-time-logs?page=${page}&limit=${pagination.limit}`, {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();

      // API returns { devices: { "10_5_1_91": {...}, "10_5_1_95": {...} }, pagination: {...} }
      // Convert to old format for backwards compatibility
      const convertedLogs = { device91: [], device95: [] };
      if (data.devices) {
        Object.keys(data.devices).forEach(deviceKey => {
          const device = data.devices[deviceKey];
          if (device.ip === '10.5.1.91') {
            convertedLogs.device91 = device.logs || [];
          } else if (device.ip === '10.5.1.95') {
            convertedLogs.device95 = device.logs || [];
          }
        });
      }

      setLogs(convertedLogs);
      if (data.pagination) {
        setPagination(data.pagination);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch ADB logs:', err);
      setError(err.message);
      // Set empty logs on error to prevent undefined access
      setLogs({ device91: [], device95: [] });
    } finally {
      setLoading(false);
    }
  }

  async function startMonitoring() {
    try {
      const response = await fetch('/api/adb-monitor/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          interval: parseInt(monitorInterval),
          devices: ['10.5.1.91', '10.5.1.95']
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      setIsMonitoring(true);
      setCurrentPage(1); // Go to first page when starting monitoring
      fetchLogs(1); // Refresh logs immediately
    } catch (err) {
      console.error('Failed to start monitoring:', err);
      alert('Failed to start monitoring: ' + err.message);
    }
  }

  async function stopMonitoring() {
    try {
      const response = await fetch('/api/adb-monitor/stop', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error(await response.text());
      setIsMonitoring(false);
    } catch (err) {
      console.error('Failed to stop monitoring:', err);
      alert('Failed to stop monitoring: ' + err.message);
    }
  }

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      padding: '1rem',
    },
    modal: {
      backgroundColor: '#0d1623',
      borderRadius: '12px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
      width: '100%',
      maxWidth: '1400px',
      maxHeight: 'calc(100vh - 100px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      border: '1px solid #23314a',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1.5rem 2rem',
      borderBottom: '1px solid #23314a',
      backgroundColor: '#0f1a29',
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: 'bold',
      color: '#ffffff',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#9ca3af',
      fontSize: '2rem',
      cursor: 'pointer',
      padding: '0',
      lineHeight: '1',
      transition: 'color 0.2s',
    },
    controls: {
      padding: '1.5rem 2rem',
      backgroundColor: '#101b2b',
      borderBottom: '1px solid #23314a',
      display: 'flex',
      gap: '1.5rem',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    controlGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    label: {
      fontSize: '0.875rem',
      color: '#9ca3af',
    },
    select: {
      padding: '0.5rem 0.75rem',
      backgroundColor: '#0d1623',
      border: '1px solid #23314a',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '0.875rem',
      cursor: 'pointer',
    },
    buttonGreen: {
      padding: '0.5rem 1.5rem',
      backgroundColor: '#16a34a',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    buttonRed: {
      padding: '0.5rem 1.5rem',
      backgroundColor: '#dc2626',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    statusIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#4ade80',
      fontSize: '0.875rem',
    },
    pulse: {
      width: '8px',
      height: '8px',
      backgroundColor: '#4ade80',
      borderRadius: '50%',
      animation: 'pulse 2s infinite',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: '2rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '2rem',
      height: '100%',
    },
    deviceCard: {
      backgroundColor: '#101b2b',
      borderRadius: '12px',
      padding: '1.5rem',
      border: '1px solid #23314a',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100%',
    },
    deviceTitle: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      marginBottom: '1.5rem',
    },
    tableContainer: {
      flex: 1,
      overflow: 'auto',
    },
    table: {
      width: '100%',
      fontSize: '0.875rem',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    th: {
      textAlign: 'left',
      padding: '0.75rem 1rem',
      color: '#9ca3af',
      fontWeight: '600',
      borderBottom: '1px solid #23314a',
      position: 'sticky',
      top: 0,
      backgroundColor: '#101b2b',
    },
    td: {
      padding: '0.75rem 1rem',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontSize: '0.8125rem',
      borderBottom: '1px solid #1f2b40',
    },
    footer: {
      padding: '1.5rem 2rem',
      borderTop: '1px solid #23314a',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#0f1a29',
    },
    footerText: {
      fontSize: '0.875rem',
      color: '#9ca3af',
    },
    buttonClose: {
      padding: '0.5rem 1.5rem',
      backgroundColor: '#4a7dff',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    paginationContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    pageButton: {
      padding: '0.5rem 0.75rem',
      backgroundColor: '#1f2b40',
      border: '1px solid #23314a',
      borderRadius: '6px',
      color: '#ffffff',
      fontSize: '0.875rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    pageButtonDisabled: {
      padding: '0.5rem 0.75rem',
      backgroundColor: '#0d1623',
      border: '1px solid #23314a',
      borderRadius: '6px',
      color: '#4b5563',
      fontSize: '0.875rem',
      cursor: 'not-allowed',
      opacity: 0.5,
    },
    pageInfo: {
      fontSize: '0.875rem',
      color: '#9ca3af',
      padding: '0 0.5rem',
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          /* Mobile Responsiveness */
          @media (max-width: 768px) {
            .adb-modal-overlay {
              padding: 0.5rem !important;
            }

            .adb-modal {
              max-height: 100vh !important;
            }

            .adb-modal-header {
              padding: 1rem !important;
            }

            .adb-modal-title {
              font-size: 1.25rem !important;
            }

            .adb-modal-controls {
              padding: 1rem !important;
              flex-direction: column !important;
              align-items: stretch !important;
              gap: 1rem !important;
            }

            .adb-modal-control-group {
              flex-direction: column !important;
              align-items: stretch !important;
              gap: 0.5rem !important;
            }

            .adb-modal-control-group label {
              width: 100% !important;
            }

            .adb-modal-content {
              padding: 1rem !important;
            }

            .adb-modal-grid {
              grid-template-columns: 1fr !important;
              gap: 1rem !important;
            }

            .adb-device-card {
              padding: 1rem !important;
            }

            .adb-device-title {
              font-size: 1rem !important;
              margin-bottom: 1rem !important;
            }

            .adb-table {
              font-size: 0.75rem !important;
            }

            .adb-table th,
            .adb-table td {
              padding: 0.5rem 0.5rem !important;
              font-size: 0.75rem !important;
            }

            .adb-modal-footer {
              padding: 1rem !important;
              flex-direction: column-reverse !important;
              gap: 1rem !important;
            }

            .adb-pagination-container {
              width: 100% !important;
              flex-wrap: wrap !important;
              justify-content: center !important;
            }

            .adb-page-button,
            .adb-page-button-disabled {
              padding: 0.5rem 0.5rem !important;
              font-size: 0.75rem !important;
              min-width: 60px !important;
            }

            .adb-page-info {
              width: 100% !important;
              text-align: center !important;
              order: -1 !important;
              margin-bottom: 0.5rem !important;
              font-size: 0.75rem !important;
            }

            .adb-button-close {
              width: 100% !important;
            }
          }

          @media (max-width: 480px) {
            .adb-modal-title {
              font-size: 1rem !important;
            }

            .adb-device-title {
              font-size: 0.875rem !important;
            }

            .adb-table th,
            .adb-table td {
              padding: 0.4rem 0.3rem !important;
              font-size: 0.6875rem !important;
            }

            .adb-page-button,
            .adb-page-button-disabled {
              padding: 0.4rem 0.4rem !important;
              font-size: 0.6875rem !important;
              min-width: 50px !important;
            }
          }
        `}
      </style>
      <div className="adb-modal" style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="adb-modal-header" style={styles.header}>
          <h2 className="adb-modal-title" style={styles.title}>ADB Time Monitor - Lowes LTE 1A</h2>
          <button
            onClick={onClose}
            style={styles.closeButton}
            onMouseOver={(e) => e.target.style.color = '#ffffff'}
            onMouseOut={(e) => e.target.style.color = '#9ca3af'}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Controls */}
        <div className="adb-modal-controls" style={styles.controls}>
          <div className="adb-modal-control-group" style={styles.controlGroup}>
            <label style={styles.label}>Monitoring Interval:</label>
            <select
              value={monitorInterval}
              onChange={(e) => setMonitorInterval(e.target.value)}
              disabled={isMonitoring}
              style={{...styles.select, opacity: isMonitoring ? 0.5 : 1}}
            >
              <option value="5">5 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">60 minutes</option>
              <option value="120">2 hours</option>
            </select>
          </div>

          {!isMonitoring ? (
            <button
              onClick={startMonitoring}
              style={styles.buttonGreen}
              onMouseOver={(e) => e.target.style.backgroundColor = '#15803d'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#16a34a'}
            >
              Start Monitoring
            </button>
          ) : (
            <button
              onClick={stopMonitoring}
              style={styles.buttonRed}
              onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
            >
              Stop Monitoring
            </button>
          )}

          {isMonitoring && (
            <div style={styles.statusIndicator}>
              <span style={styles.pulse}></span>
              <span>Active</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="adb-modal-content" style={styles.content}>
          {loading && (
            <div style={{textAlign: 'center', padding: '3rem', color: '#9ca3af'}}>
              Loading logs...
            </div>
          )}

          {error && (
            <div style={{textAlign: 'center', padding: '3rem', color: '#ef4444'}}>
              Error: {error}
            </div>
          )}

          {!loading && !error && (
            <div className="adb-modal-grid" style={styles.grid}>
              {/* Device 10.5.1.91 */}
              <div className="adb-device-card" style={styles.deviceCard}>
                <h3 className="adb-device-title" style={{...styles.deviceTitle, color: '#60a5fa'}}>Device 10.5.1.91:5555</h3>
                <div style={styles.tableContainer}>
                  <table className="adb-table" style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Local Time</th>
                        <th style={styles.th}>Device Time</th>
                        <th style={styles.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.device91.length === 0 ? (
                        <tr>
                          <td colSpan="3" style={{...styles.td, textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                            No logs available
                          </td>
                        </tr>
                      ) : (
                        logs.device91.map((entry, idx) => (
                          <tr key={idx} style={{transition: 'background-color 0.2s'}}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#132038'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <td style={{...styles.td, color: '#d1d5db'}}>{entry.localTime}</td>
                            <td style={styles.td}>{entry.deviceTime || '—'}</td>
                            <td style={styles.td}>
                              <span style={{color: entry.status === 'success' ? '#4ade80' : '#ef4444'}}>
                                {entry.status === 'success' ? '✓' : '✗'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Device 10.5.1.95 */}
              <div className="adb-device-card" style={styles.deviceCard}>
                <h3 className="adb-device-title" style={{...styles.deviceTitle, color: '#4ade80'}}>Device 10.5.1.95:5555</h3>
                <div style={styles.tableContainer}>
                  <table className="adb-table" style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Local Time</th>
                        <th style={styles.th}>Device Time</th>
                        <th style={styles.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.device95.length === 0 ? (
                        <tr>
                          <td colSpan="3" style={{...styles.td, textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                            No logs available
                          </td>
                        </tr>
                      ) : (
                        logs.device95.map((entry, idx) => (
                          <tr key={idx} style={{transition: 'background-color 0.2s'}}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#132038'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <td style={{...styles.td, color: '#d1d5db'}}>{entry.localTime}</td>
                            <td style={styles.td}>{entry.deviceTime || '—'}</td>
                            <td style={styles.td}>
                              <span style={{color: entry.status === 'success' ? '#4ade80' : '#ef4444'}}>
                                {entry.status === 'success' ? '✓' : '✗'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="adb-modal-footer" style={styles.footer}>
          <div className="adb-pagination-container" style={styles.paginationContainer}>
            <button
              className={currentPage === 1 ? "adb-page-button-disabled" : "adb-page-button"}
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={currentPage === 1 ? styles.pageButtonDisabled : styles.pageButton}
              onMouseOver={(e) => {
                if (currentPage !== 1) e.target.style.backgroundColor = '#2a3f5f';
              }}
              onMouseOut={(e) => {
                if (currentPage !== 1) e.target.style.backgroundColor = '#1f2b40';
              }}
            >
              First
            </button>
            <button
              className={currentPage === 1 ? "adb-page-button-disabled" : "adb-page-button"}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={currentPage === 1 ? styles.pageButtonDisabled : styles.pageButton}
              onMouseOver={(e) => {
                if (currentPage !== 1) e.target.style.backgroundColor = '#2a3f5f';
              }}
              onMouseOut={(e) => {
                if (currentPage !== 1) e.target.style.backgroundColor = '#1f2b40';
              }}
            >
              Previous
            </button>
            <span className="adb-page-info" style={styles.pageInfo}>
              Page {currentPage} of {Math.max(pagination.totalPages91, pagination.totalPages95)}
              ({pagination.totalCount91 + pagination.totalCount95} total logs)
            </span>
            <button
              className={currentPage >= Math.max(pagination.totalPages91, pagination.totalPages95) ? "adb-page-button-disabled" : "adb-page-button"}
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={currentPage >= Math.max(pagination.totalPages91, pagination.totalPages95)}
              style={currentPage >= Math.max(pagination.totalPages91, pagination.totalPages95) ? styles.pageButtonDisabled : styles.pageButton}
              onMouseOver={(e) => {
                if (currentPage < Math.max(pagination.totalPages91, pagination.totalPages95)) {
                  e.target.style.backgroundColor = '#2a3f5f';
                }
              }}
              onMouseOut={(e) => {
                if (currentPage < Math.max(pagination.totalPages91, pagination.totalPages95)) {
                  e.target.style.backgroundColor = '#1f2b40';
                }
              }}
            >
              Next
            </button>
            <button
              className={currentPage >= Math.max(pagination.totalPages91, pagination.totalPages95) ? "adb-page-button-disabled" : "adb-page-button"}
              onClick={() => setCurrentPage(Math.max(pagination.totalPages91, pagination.totalPages95))}
              disabled={currentPage >= Math.max(pagination.totalPages91, pagination.totalPages95)}
              style={currentPage >= Math.max(pagination.totalPages91, pagination.totalPages95) ? styles.pageButtonDisabled : styles.pageButton}
              onMouseOver={(e) => {
                if (currentPage < Math.max(pagination.totalPages91, pagination.totalPages95)) {
                  e.target.style.backgroundColor = '#2a3f5f';
                }
              }}
              onMouseOut={(e) => {
                if (currentPage < Math.max(pagination.totalPages91, pagination.totalPages95)) {
                  e.target.style.backgroundColor = '#1f2b40';
                }
              }}
            >
              Last
            </button>
          </div>
          <button
            className="adb-button-close"
            onClick={onClose}
            style={styles.buttonClose}
            onMouseOver={(e) => e.target.style.backgroundColor = '#3a6de0'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#4a7dff'}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
